import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Mail, Loader2, Send, Eye, RotateCcw, PauseCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { ukDate } from '../../lib/format';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  sent: 'bg-green-100 text-green-800',
  logged: 'bg-amber-100 text-amber-800',
  failed: 'bg-red-100 text-red-700',
};

// Preview and send the booking confirmation, plus this job's email log
export const JobEmailPanel = ({ job, token }) => {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [showText, setShowText] = useState(false);
  const [log, setLog] = useState(null);
  const [showLog, setShowLog] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const loadLog = async () => {
    try {
      const res = await axios.get(`${API}/admin/jobs/${job.id}/emails`, { headers });
      setLog(res.data);
    } catch (err) {
      toast.error('Could not load the email log');
    }
  };

  useEffect(() => { if (showLog && !log) loadLog(); /* eslint-disable-next-line */ }, [showLog]);

  const openPreview = async () => {
    setOpen(true);
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/admin/jobs/${job.id}/emails/booking_confirmation/preview`, { headers });
      setPreview(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not build that preview');
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const send = async () => {
    setSending(true);
    try {
      const res = await axios.post(
        `${API}/admin/jobs/${job.id}/emails/booking_confirmation/send`, {}, { headers });
      if (res.data.status === 'sent') toast.success(`Confirmation sent to ${res.data.to}`);
      else if (res.data.status === 'logged') toast.message('No mailbox password here, so it was written to the log instead');
      else toast.error('That email could not be sent');
      setOpen(false);
      setLog(null);
      if (showLog) loadLog();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not send that email');
    } finally {
      setSending(false);
    }
  };

  const resend = async (entry) => {
    try {
      await axios.post(`${API}/admin/emails/log/${entry.id}/resend`, {}, { headers });
      toast.success('Sent again');
      loadLog();
    } catch (err) {
      toast.error('Could not resend that email');
    }
  };

  const togglePause = async (paused) => {
    try {
      await axios.put(`${API}/admin/jobs/${job.id}/emails/pause`, { paused }, { headers });
      setLog({ ...log, emails_paused: paused });
      toast.success(paused ? 'Emails paused for this job' : 'Emails back on for this job');
    } catch (err) {
      toast.error('Could not change that switch');
    }
  };

  return (
    <div className="mt-3 flex flex-wrap items-center gap-2" data-testid={`job-emails-${job.id}`}>
      <Button variant="outline" size="sm" onClick={openPreview}
              data-testid={`preview-confirmation-${job.id}`}>
        <Eye className="mr-1 h-4 w-4" /> Preview confirmation
      </Button>
      <Button variant="ghost" size="sm" onClick={() => setShowLog(!showLog)}
              data-testid={`toggle-email-log-${job.id}`}>
        <Mail className="mr-1 h-4 w-4" /> {showLog ? 'Hide emails' : 'Emails'}
      </Button>

      {showLog && (
        <div className="w-full rounded-[10px] border border-hair bg-page p-3"
             data-testid={`email-log-${job.id}`}>
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs text-slate-500">
              {log?.customer_email ? `Customer: ${log.customer_email}` : 'No customer email on this job'}
            </p>
            <label className="flex items-center gap-2 text-xs font-semibold text-ink">
              <PauseCircle className="h-4 w-4 text-gold-deep" /> Pause emails
              <Switch checked={!!log?.emails_paused}
                      onCheckedChange={togglePause}
                      aria-label="Pause emails for this job"
                      data-testid={`pause-emails-${job.id}`} />
            </label>
          </div>

          {!log && <p className="text-xs text-slate-400">Loading…</p>}
          {log && log.emails.length === 0 && (
            <p className="text-xs text-slate-500">No emails sent for this job yet.</p>
          )}
          {log?.emails.map((entry) => (
            <div key={entry.id}
                 className="flex flex-wrap items-center justify-between gap-2 border-b border-hair py-2 last:border-0">
              <div className="min-w-0">
                <p className="text-xs font-bold text-ink">{entry.label}</p>
                <p className="text-xs text-slate-500">
                  {ukDate(entry.created_at, { withYear: true })} · {entry.to}
                  {entry.error ? ` · ${entry.error}` : ''}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${STATUS_STYLES[entry.status] || 'bg-cream text-slate-700'}`}>
                  {entry.status}
                </span>
                <Button variant="ghost" size="sm" onClick={() => resend(entry)}
                        data-testid={`resend-email-${entry.id}`}>
                  <RotateCcw className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="text-base">Booking confirmation</DialogTitle>
          </DialogHeader>

          {loading && <p className="flex items-center gap-2 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Building the preview…
          </p>}

          {preview && (
            <div className="space-y-3">
              <div className="rounded-[10px] border border-hair bg-cream p-3 text-sm">
                <p className="text-ink"><strong>To:</strong> {preview.to || '— no customer email on this job'}</p>
                <p className="mt-1 text-ink"><strong>Subject:</strong> {preview.subject}</p>
                {preview.already_sent && (
                  <p className="mt-1 text-xs font-semibold text-amber-700" data-testid="already-sent-warning">
                    A confirmation has already been sent for this job.
                  </p>
                )}
                {preview.blocked_reason && (
                  <p className="mt-1 text-xs font-semibold text-red-700" data-testid="blocked-warning">
                    {preview.blocked_reason}
                  </p>
                )}
                {!preview.smtp_ready && (
                  <p className="mt-1 text-xs text-slate-500">
                    No mailbox password here, so sending will write to the log instead.
                  </p>
                )}
              </div>

              <div className="flex gap-2">
                <Button variant={showText ? 'ghost' : 'secondary'} size="sm"
                        onClick={() => setShowText(false)} data-testid="preview-html-btn">HTML</Button>
                <Button variant={showText ? 'secondary' : 'ghost'} size="sm"
                        onClick={() => setShowText(true)} data-testid="preview-text-btn">Plain text</Button>
              </div>

              {showText ? (
                <pre className="max-h-[55vh] overflow-auto whitespace-pre-wrap rounded-[10px] border border-hair bg-white p-3 text-xs text-ink"
                     data-testid="email-preview-text">{preview.text}</pre>
              ) : (
                <iframe title="Email preview" srcDoc={preview.html}
                        className="h-[55vh] w-full rounded-[10px] border border-hair bg-white"
                        data-testid="email-preview-frame" />
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Close</Button>
            <Button onClick={send}
                    disabled={sending || !preview?.to || !!preview?.blocked_reason}
                    data-testid={`send-confirmation-${job.id}`}>
              {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                : <Send className="mr-2 h-4 w-4" />}
              Send to the customer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JobEmailPanel;
